import Skeleton from "Components/Common/Skeleton/Skeleton";
import Button from "Components/Common/Button/Button";
import ProgressBar from "Components/Common/ProgressBar/ProgressBar";
import NoData from "Pages/Admin/InspectorAssign/components/NoData";
import { OverviewData } from "../OwnerProjectDetail";
import { formatStatus } from "Pages/GcPrecon/GcProjectDetails/components/DrawHistoryTable";
import { formatDate } from "utils/Common/Date";

interface Props {
  overviewData: OverviewData | null;
  bankName: string;
  aiaSigned: boolean;
  showSection: (id: string) => void;
  loading?: boolean;
}

const ACTIVITY_ICON: Record<string, string> = {
  PROJECT_UPDATED: "📋",
  PRECON_SUBMITTED: "📄",
  PROJECT_PUBLISHED: "📋",
  CHECKLIST_PUBLISHED: "✅",
  CHECKLIST_DRAFT_SAVED: "💾",
  GC_REQUIREMENTS_PUBLISHED: "✅",
  PROJECT_CREATED: "🏗",
  DRAW_SUBMITTED: "📤",
  DRAW_APPROVED: "✅",
  DRAW_REJECTED: "❌",
  INSPECTION_COMPLETED: "🔍",
  CHANGE_ORDER_SUBMITTED: "📝",
  CHANGE_ORDER_APPROVED: "✅",
};

function formatAmount(amount?: number, symbol = "$"): string {
  if (amount == null) return "—";
  return `${symbol}${amount.toLocaleString("en-US")}`;
}

function OverviewSkeleton() {
  return (
    <>
      <div className="welcome-banner">
        <div className="wb-left">
          <div className="skeletonStack">
            <Skeleton width={120} height={32} />
            <Skeleton width={200} height={14} />
            <Skeleton width={180} height={14} />
          </div>
        </div>
        <div className="wb-stats">
          <div className="wb-stat">
            <div className="skeletonStatStack">
              <Skeleton width={60} height={28} />
              <Skeleton width={60} height={12} />
            </div>
          </div>
          <div className="wb-stat">
            <div className="skeletonStatStack">
              <Skeleton width={60} height={28} />
              <Skeleton width={60} height={12} />
            </div>
          </div>
        </div>
      </div>

      <div className="metrics">
        {[1, 2, 3, 4].map((i) => (
          <div className="metric" key={i}>
            <div className="skeletonMetricStack">
              <Skeleton width={80} height={12} />
              <Skeleton width={60} height={22} />
              <Skeleton width={100} height={11} />
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <Skeleton width={180} height={16} className="skeletonSectionTitle" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div className="prog-row" key={i}>
            <Skeleton width={160} height={12} />
            <div className="flexFill">
              <Skeleton width="100%" height={8} borderRadius={4} />
            </div>
            <Skeleton width={36} height={12} />
          </div>
        ))}
      </div>

      <div className="card">
        <Skeleton width={130} height={16} className="skeletonSectionTitle" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="skeletonActivityRow">
            <Skeleton width={20} height={20} borderRadius="50%" />
            <div className="flexFill skeletonStack">
              <Skeleton width="60%" height={13} />
              <Skeleton width="40%" height={11} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export default function OwnerOverviewSection({
  overviewData,
  bankName,
  aiaSigned,
  loading,
  showSection,
}: Props) {
  if (loading) return <OverviewSkeleton />;

  const firstName =
    JSON.parse(localStorage.getItem("user") || "{}").firstName ?? "";
  const project = overviewData?.project;
  const drawRequest = overviewData?.drawRequest;
  const report = overviewData?.report;
  const snapshot = report?.inspectionSnapshot;
  const phases = snapshot?.phases ?? [];
  const lineItems = snapshot?.lineItems ?? [];
  const isCommercial = phases.length === 0 && lineItems.length > 0;
  const overallEstimation = snapshot?.overallCscEstimation ?? 0;
  const currencySymbol =
    overviewData?.contractUpdatedValue?.currency?.symbol ?? "$";

  return (
    <>
      <div className="welcome-banner">
        <div className="wb-left">
          <h1>Welcome{firstName ? `, ${firstName}` : ""}.</h1>
          <p>
            {project?.name ?? "—"}
            <br />
            {project?.address && <> {project.address}</>} · {bankName}
          </p>
        </div>
        <div className="wb-stats">
          <div className="wb-stat">
            <div className="wb-val">
              {project?.progressPercentage
                ? `${parseFloat(project.progressPercentage).toFixed(0)}%`
                : "—"}
            </div>
            <div className="wb-label">Complete</div>
          </div>
          <div className="wb-stat">
            <div className="wb-val">
              {formatAmount(
                overviewData?.contractUpdatedValue?.amount,
                currencySymbol,
              )}
            </div>
            <div className="wb-label">Contract</div>
          </div>
          {drawRequest && (
            <div className="wb-stat">
              <div className="wb-val">Draw #{drawRequest?.drawNumber}</div>
              <div className="wb-label">Current</div>
            </div>
          )}
        </div>
      </div>

      {!aiaSigned &&
        project?.projectStage === "owner_draw_request_signature" && (
          <div className="action-required">
            <span className="ar-icon">✍️</span>
            <div className="ar-body">
              <div className="ar-title">
                Your Signature is Required — Draw #
                {String((drawRequest?.drawNumber ?? 0) + 1).padStart(2, "0")}
              </div>
              <div className="ar-text">
                A draw request has been submitted. As the Owner/Borrower, your
                signature on the AIA Application for Payment is required
                before this draw can be processed by {bankName}. Please
                review and sign at your earliest convenience.
              </div>
            </div>
            <Button className="ar-btn" onClick={() => showSection("sign-aia")}>
              Sign Now →
            </Button>
          </div>
        )}

      <div className="metrics">
        <div className="metric">
          <div className="metric-label">Contract Value</div>
          <div className="metric-val">
            {formatAmount(
              overviewData?.contractUpdatedValue?.amount,
              currencySymbol,
            )}
          </div>
          <div className="metric-sub">Updated contract value</div>
        </div>
        <div className="metric">
          <div className="metric-label">Total Disbursed</div>
          <div className="metric-val">
            {formatAmount(
              overviewData?.totalDisbursedAmount?.amount,
              currencySymbol,
            )}
          </div>
          <div className="metric-sub">Approved draws</div>
        </div>
        <div className="metric">
          <div className="metric-label">Balance to Finish</div>
          <div className="metric-val">
            {formatAmount(
              overviewData?.balanceToFinish?.amount,
              currencySymbol,
            )}
          </div>
          <div className="metric-sub">Incl. retainage held</div>
        </div>
        <div className="metric">
          <div className="metric-label">Project Status</div>
          <div className="metric-val metricValAmber">
            {project?.status ? formatStatus(project.status) : "—"}
          </div>
          <div className="metric-sub">
            {project?.estCompletionDate
              ? `Est. completion ${formatDate(project.estCompletionDate, false)}`
              : "—"}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          Construction Progress
          <span className="read-only-badge">From CSC Inspections</span>
        </div>

        {/* Overall */}
        <div className="prog-row">
          <span className="prog-label progLabelBold">Overall Completion</span>
          <div className="flexFill">
            <ProgressBar value={overallEstimation} />
          </div>
          <span className="prog-val">
            {overallEstimation ? Math.round(overallEstimation) : 0}%
          </span>
        </div>

        <div className="progDivider" />

        {/* Commercial: flat line items */}
        {isCommercial
          ? lineItems.map((item) => (
              <div key={item.id} className="prog-row progRowSpaced">
                <div className="prog-label commercialLabelRow">
                  <span className="commercialItemCode">
                    {item.aiaItem.itemLabel} —
                  </span>
                  <span className="commercialItemName">
                    {item.aiaItem.itemName}
                  </span>
                </div>
                <div className="flexFill">
                  <ProgressBar
                    value={item.cscEstimation}
                    isFlagged={item.isFlagged}
                  />
                </div>
                <span
                  className={`prog-val progValSm${item.isFlagged ? " progValFlagged" : ""}`}
                >
                  {item.cscEstimation ? Math.round(item.cscEstimation) : "0"}%
                </span>
              </div>
            ))
          : /* Residential: phases + items */
            [...phases]
              .sort((a, b) => a.phaseOrder - b.phaseOrder)
              .map((phase) => (
                <div key={phase.phase} className="phaseGroup">
                  <div className="prog-row phaseHeaderRow">
                    <span className="prog-label phaseHeaderLabel">
                      Phase {phase.phase}
                    </span>
                    <div className="flexFill">
                      <ProgressBar value={phase.phaseCscEstimation} />
                    </div>
                    <span className="prog-val progValBold">
                      {phase.phaseCscEstimation
                        ? Math.round(phase.phaseCscEstimation)
                        : 0}
                      %
                    </span>
                  </div>

                  {phase.items.map((item) => (
                    <div key={item.id} className="prog-row phaseItemRow">
                      <span className="prog-label phaseItemLabel">
                        {item.itemLabel}
                      </span>
                      <div className="flexFill">
                        <ProgressBar
                          value={item.cscEstimation}
                          isFlagged={item.isFlagged}
                        />
                      </div>
                      <span
                        className={`prog-val progValSm${item.isFlagged ? " progValFlagged" : ""}`}
                      >
                        {item.cscEstimation
                          ? Math.round(item.cscEstimation)
                          : 0}
                        %
                      </span>
                    </div>
                  ))}
                </div>
              ))}

        {report && (
          <div className="lastInspectionNote">
            Last inspection:{" "}
            {report.inspectionDate
              ? formatDate(report.inspectionDate, false)
              : "—"}{" "}
            {report.inspectorName && `· Inspector: ${report.inspectorName}`}
            {overviewData?.stakeholders?.admin?.companyName &&
              ` · ${overviewData.stakeholders.admin.companyName}`}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title">Recent Activity</div>
        {!overviewData?.recentActivities?.activities?.length && (
          <NoData text="No recent activity" />
        )}
        <div className="activityList">
          {(overviewData?.recentActivities?.activities ?? []).map(
            (activity) => {
              const icon = ACTIVITY_ICON[activity.type] ?? "📌";
              return (
                <div
                  key={`${activity.type}-${activity.date}`}
                  className="activityRow"
                >
                  <span className="activityIcon">{icon}</span>
                  <div>
                    <div className="activityTitle">{activity.title}</div>
                    <div className="activityMeta">
                      {[
                        formatDate(activity.date, false),
                        activity.performedBy,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                </div>
              );
            },
          )}
        </div>
      </div>
    </>
  );
}
